import type { WindowLocation } from '@reach/router';
import { use, useMemo } from 'react';

import type { ClubCmsRecord } from 'src/content/cms-provider/club-cms-provider';
import { clubPageIsDisabledInCms } from 'src/content/optional-page-helper';
import { SiteContext } from 'src/context/site-context';
import { useGatsbyPaths } from 'src/custom-hooks/use-gatsby-paths';
import { isExternalLink } from 'src/helpers/link-helper';
import { getClubPagePath, getGlobalPagePath } from 'src/helpers/navigation-helper';
import { isOnClubPage, isOnGlobalPage } from 'src/helpers/path-matching-helper';

type UseLinkParams = {
  goToGlobal?: boolean;
  toPath: string;
  location?: WindowLocation;
  enabledClubPages?: ClubCmsRecord['pages'];
};

type UseLinkResult = {
  translatedPath: string;
  isInternalLink: boolean;
  isPageDisabledInCms: boolean;
  isHighlight: boolean;
  useGatsbyPath: boolean;
};

export const useLink = ({
  toPath,
  goToGlobal,
  location,
  enabledClubPages,
}: UseLinkParams): UseLinkResult => {
  const { isGlobalPage, language, buildType, siteId } = use(SiteContext);

  const { isGatsbyPath } = useGatsbyPaths();
  const isInternalLink = useMemo(() => !(toPath && isExternalLink(toPath)), [toPath]);

  const path = useMemo(() => {
    const internalPath =
      isGlobalPage || goToGlobal
        ? getGlobalPagePath(toPath, language, buildType)
        : getClubPagePath(toPath, siteId, language);
    return isInternalLink ? internalPath : toPath;
  }, [isGlobalPage, language, buildType, siteId, goToGlobal, toPath, isInternalLink]);

  const useGatsbyPath = useMemo(() => isGatsbyPath(path), [isGatsbyPath, path]);

  const isClubPageDisabled = useMemo(
    () => (enabledClubPages ? clubPageIsDisabledInCms(toPath, enabledClubPages) : false),
    [toPath, enabledClubPages]
  );

  const isPageDisabledInCms = useMemo(
    () => !isGlobalPage && isClubPageDisabled,
    [isGlobalPage, isClubPageDisabled]
  );

  const isHighlight = useMemo(() => {
    if (!location) return false;

    return isGlobalPage
      ? isOnGlobalPage(location, toPath, language)
      : isOnClubPage(location, siteId, toPath, language);
  }, [isGlobalPage, language, location, toPath, siteId]);

  return {
    translatedPath: path,
    isInternalLink,
    isPageDisabledInCms,
    isHighlight,
    useGatsbyPath,
  };
};
